import { useToast } from '@shared/hooks/use-toast';
import { faArrowRight, faXmark, faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Product } from '@shared/components/ui/Product';
import { StepperInput } from '@shared/components/ui/StepperInput';
import { Badge } from '@shared/components/ui/badge';
import { getErrorMessage } from '@shared/utils/error.utils';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BatchSelect } from '@modules/Batches/components/BatchSelect';
import { Location } from '../../models/Location';
import { ProductSelector, SelectedProduct } from '../interaction/ProductSelector';

// 🎯 Extender la interfaz del ref para incluir método personalizado
interface ExtendedModalCenterRef extends ModalCenterRef {
  openWithLocation: (location: Location) => void;
}

interface AdjustLocationModalProps {
  onSuccess?: () => void;
}

const AdjustLocationModal = forwardRef<ExtendedModalCenterRef, AdjustLocationModalProps>(({ onSuccess }, ref) => {
  const { t } = useTranslation('locationsModule');
  const { toast } = useToast();

  // 🎯 Referencia interna al ModalCenter para evitar recursión
  const internalModalRef = useRef<ModalCenterRef>(null);

  // 🎯 Estado interno del modal - NO causa re-render del padre
  const [location, setLocation] = useState<Location | null>(null);
  const [quantity, setQuantity] = useState(0);
  const [reason, setReason] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<SelectedProduct | null>(null);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);

  const resetForm = () => {
    setLocation(null);
    setQuantity(0);
    setReason('');
    setSelectedProduct(null);
    setSelectedBatchId(null);
  };

  const handleCancel = () => {
    internalModalRef.current?.closeModal();
    resetForm();
  };

  const handleProductSelect = (product: SelectedProduct) => {
    setSelectedProduct(product);
    setQuantity(0);
    setSelectedBatchId(null);
  };

  const clearSelectedProduct = () => {
    setSelectedProduct(null);
    setQuantity(0);
  };

  // Servicio
  const service = useMemo(async () => {
    const httpClient = (await import('@adapters/HttpClient')).createHttpClient(
      (await import('@shared/services/ApiClient')).apiClientWH,
    );
    const { LocationApiAdapter } = await import('../../adapters/LocationApiAdapter');
    const { LocationService } = await import('../../services/LocationService');
    return new LocationService(new LocationApiAdapter(httpClient));
  }, []);

  const handleAdjust = async () => {
    try {
      if (!location) {
        toast({
          title: t('errors.generic'),
          description: t('modal.adjust.error_no_selection'),
          variant: 'destructive',
        });
        return;
      }

      // 🎯 Validar que hay producto (existente o seleccionado)
      const hasProduct = location.product?.name || selectedProduct;
      if (!hasProduct) {
        toast({
          title: t('errors.generic'),
          description: t('modal.adjust.error_no_product'),
          variant: 'destructive',
        });
        return;
      }

      if (quantity === 0) {
        toast({
          title: t('errors.generic'),
          description: t('modal.adjust.error_zero_quantity'),
          variant: 'destructive',
        });
        return;
      }

      if (newStock < 0) {
        toast({
          title: t('errors.generic'),
          description: t('modal.adjust.error_negative_stock', {
            available: currentStock,
          }),
          variant: 'destructive',
        });
        return;
      }

      if (!reason.trim()) {
        toast({
          title: t('errors.generic'),
          description: t('modal.adjust.error_no_reason'),
          variant: 'destructive',
        });
        return;
      }

      const productId = hasLocationProduct
        ? (Array.isArray(location.product) ? location.product[0]?.id : (location.product as any).id) ||
          selectedProduct?.id
        : selectedProduct?.id;

      if (!productId) {
        toast({
          title: t('errors.generic'),
          description: t('modal.adjust.error_no_product'),
          variant: 'destructive',
        });
        return;
      }

      // Llamada real a la API
      const svc = await service;
      await svc.adjustStock({
        product_id: Number(productId),
        qty: Number(quantity),
        location_id: Number(location.id),
        description: reason || undefined,
        ...(selectedBatchId && { batch_id: selectedBatchId }),
      });

      toast({
        title: t('modal.adjust.success_title'),
        description: t('modal.adjust.success_description'),
        variant: 'default',
      });

      resetForm();
      handleCancel();
      onSuccess?.();
    } catch (error: any) {
      toast({
        title: t('modal.adjust.error_title'),
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    }
  };

  const currentStock = location?.stock || 0;
  const newStock = currentStock + quantity;
  const minQuantity = -currentStock; // No puede eliminar más de lo que hay

  const handleQuantityChange = (value: number) => {
    // Validar que no exceda los límites
    if (value < minQuantity) {
      setQuantity(minQuantity);
      return;
    }
    if (value > 999) {
      setQuantity(999);
      return;
    }
    setQuantity(value);
  };

  // 🎯 Determinar qué producto mostrar
  const hasLocationProduct =
    location?.product && (Array.isArray(location.product) ? location.product.length > 0 : location.product.name);
  const displayProduct = hasLocationProduct ? location.product : selectedProduct;

  // 🎯 ÚNICO useCallback necesario - Se pasa a useImperativeHandle
  const openWithLocation = useCallback((locationData: Location) => {
    setLocation(locationData);
    internalModalRef.current?.openModal();
  }, []);

  // 🎯 Exponer métodos personalizados usando useImperativeHandle
  useImperativeHandle(
    ref,
    () => ({
      openModal: () => internalModalRef.current?.openModal(),
      closeModal: () => internalModalRef.current?.closeModal(),
      openWithLocation, // ← Método nuevo para uso directo
    }),
    [openWithLocation],
  );

  return (
    <ModalCenter
      ref={internalModalRef}
      title={
        <span className="flex items-center gap-2">
          {location?.code
            ? t('modal.adjust.title', { code: '' }) // O el texto que quieras después del badge
            : t('modal.adjust.title_fallback')}
          {location?.code && (
            <Badge variant="default" className="ml-2 font-normal text-xs text-white">
              <FontAwesomeIcon icon={faLocationDot} className="w-3 h-3 mr-1" />
              {location.code}
            </Badge>
          )}
        </span>
      }
      type="default"
      width="lg"
      className="h-[calc(100%-30px)] sm:h-auto"
    >
      <div className="space-y-6 pt-4 text-title">
        {/* Texto informativo */}
        <div className="text-sm">
          <p className=" mb-2">
            {t('modal.adjust.important_notice').split(':')[0]}
            {t('modal.adjust.important_notice').split(': ')[1]}
            <br />
            {t('modal.adjust.more_info')}{' '}
            <a
              href="https://go-warehouse.gitbook.io/warehouse-docs/software/stock"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:no-underline"
            >
              este link
            </a>
            .
          </p>
        </div>

        {/* 🎯 Producto: Selector o Mostrar existente */}
        <div className="space-y-4">
          {!hasLocationProduct && (
            <ProductSelector
              onProductSelect={handleProductSelect}
              placeholder={t('modal.adjust.product_selector_placeholder')}
              hideHelpText={!!selectedProduct}
            />
          )}
          {displayProduct && (
            <div className="flex items-center justify-between pb-3 border-b border-border">
              <Product
                id={displayProduct.id}
                name={displayProduct.name}
                src={displayProduct.image || ''}
                code={displayProduct.code || ''}
                enableLineClamp={false}
                showTooltip={false}
              />
              {/* 🎯 Botón para cambiar producto (solo si fue seleccionado, no si viene de location) */}
              {selectedProduct && !hasLocationProduct && (
                <button
                  onClick={clearSelectedProduct}
                  className="text-muted-foreground hover:text-foreground transition-colors ml-2"
                  title={t('modal.adjust.change_product')}
                >
                  <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>

        {displayProduct && (
          <BatchSelect productId={Number(displayProduct.id)} value={selectedBatchId} onChange={setSelectedBatchId} />
        )}

        {/* 🎯 Stock visual - Solo mostrar si hay producto */}
        {displayProduct && (
          <div className="flex items-center justify-center gap-8 py-4 !mt-4">
            <div className="text-center">
              <div className="text-xl font-medium pb-2">{currentStock}</div>
              <div className="text-sm font-normal">{t('modal.adjust.current_stock')}</div>
            </div>

            <FontAwesomeIcon icon={faArrowRight} className="w-5 h-5" />

            <div className="text-center">
              <div
                className={`text-xl font-medium pb-2 ${
                  quantity === 0 ? '' : quantity > 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {newStock}
              </div>
              <div className="text-sm font-normal">{t('modal.adjust.new_stock')}</div>
            </div>
          </div>
        )}

        {/* 🎯 Formulario - Solo mostrar si hay producto */}
        {displayProduct && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm mb-2 text-text">{t('modal.adjust.adjustment')}</label>
                <StepperInput value={quantity} onChange={handleQuantityChange} min={minQuantity} max={99999} />
                {quantity < 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Máximo a eliminar: {currentStock} {t('modal.adjust.units')}
                  </p>
                )}
              </div>

              <div className="col-span-2">
                <label className="block text-sm mb-2 text-text">{t('modal.adjust.reason_label')}</label>
                <Input
                  placeholder={t('modal.adjust.reason_placeholder')}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        {/* Botones */}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={handleCancel} size="lg" name="cancel">
            {t('button.cancel')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            name="confirm"
            onClick={handleAdjust}
            disabled={!displayProduct || quantity === 0}
          >
            {t('button.confirm')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

export default AdjustLocationModal;
AdjustLocationModal.displayName = 'AdjustLocationModal';
